import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ModalBox = ({ children, modal }) => {
    const [containerHovered, setContainerHovered] = useState(false);
    const [modalHovered, setModalHovered] = useState(false);
    const [isClicked, setIsClicked] = useState(false);

    const containerRef = useRef(null);
    const modalRef = useRef(null);

    // Detect mobile
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.innerWidth < 640
    );

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when mobile modal is open
    useEffect(() => {
        if (isClicked && isMobile) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isClicked, isMobile]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current && !containerRef.current.contains(event.target) &&
                (!modalRef.current || !modalRef.current.contains(event.target))
            ) {
                setIsClicked(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleContainerMouseEnter = () => {
        if (!isMobile) setContainerHovered(true);
    };

    const handleContainerMouseLeave = () => {
        if (!isMobile) {
            setTimeout(() => setContainerHovered(false), 300);
        }
    };

    const handleModalMouseEnter = () => {
        if (!isMobile) setModalHovered(true);
    };

    const handleModalMouseLeave = () => {
        if (!isMobile) {
            setTimeout(() => setModalHovered(false), 300);
        }
    };

    const handleTriggerClick = (e) => {
        e.stopPropagation();
        setIsClicked(prev => !prev);
    };

    const closeModal = (e) => {
        e.stopPropagation();
        setIsClicked(false);
        setContainerHovered(false);
        setModalHovered(false);
    };

    const isVisible = containerHovered || modalHovered || isClicked;

    // Mobile overlay rendered via portal so it's outside the trigger DOM
    const mobileOverlay = isVisible && isMobile ? createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-end justify-center"
            onClick={closeModal}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                style={{ animation: 'fadeIn 0.2s ease' }}
            />
            {/* Modal Content — stop clicks from closing */}
            <div
                ref={modalRef}
                className="relative w-full bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden"
                style={{ animation: 'slideUp 0.3s ease' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 bg-slate-200 rounded-full" />
                </div>
                {/* Header with close */}
                <div className="flex justify-between items-center px-4 pb-2">
                    <h3 className="text-base font-bold text-slate-800">Категории</h3>
                    <button
                        onClick={closeModal}
                        className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                    >
                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                {modal}
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <div ref={containerRef}>
            <div
                onMouseEnter={handleContainerMouseEnter}
                onMouseLeave={handleContainerMouseLeave}
                onClick={handleTriggerClick}
            >
                {children}
            </div>

            {/* Desktop: absolute dropdown below trigger */}
            {isVisible && !isMobile && (
                <div
                    className="relative z-50 w-full"
                    ref={modalRef}
                    onMouseEnter={handleModalMouseEnter}
                    onMouseLeave={handleModalMouseLeave}
                >
                    <div className='absolute'>
                        {modal}
                    </div>
                </div>
            )}

            {/* Mobile: portal overlay */}
            {mobileOverlay}
        </div>
    );
};

export default ModalBox;
